// Value options for a text rule (segment rule fields plan §4.3, A4, A5). Pure:
// no React, no I/O. An option is a dashboard label plus every stored value it
// stands for. Pick-list options come from the catalog's `fieldOptions`; Country
// and State options are the contact geo suggestions. A stored value no option
// covers is kept and shown raw, so nothing saved is ever lost.
import type { AudienceCondition, NativeAudienceCatalog } from '@charitystack/schemas';
// Type-only, so this module (and the sentences that use it) stays free of the geo loader's I/O imports.
import type { GeoField, GeoSuggestions } from './geoSuggestions';

type TextCondition = Extract<AudienceCondition, { type: 'text' }>;
type TextField = TextCondition['field'];

export type ValueOption = { label: string; values: string[] };
/** What the value box shows: an option, or a stored value no option covers. */
export type ValueChip = { key: string; label: string; values: string[]; known: boolean };

/** How a text field takes its values: a closed pick-list, suggestions beside free text, or free text alone. */
export type ValueSource = { kind: 'pick-list' | 'suggestions'; options: ValueOption[] } | { kind: 'free-text' };

const FREE_TEXT: ValueSource = { kind: 'free-text' };

export function valueSourceFor(field: string, catalog: Pick<NativeAudienceCatalog, 'fieldOptions'>, geo: GeoSuggestions | null | undefined): ValueSource {
  const picked = catalog.fieldOptions?.[field];
  if (picked && picked.length > 0) return { kind: 'pick-list', options: picked };
  if ((field === 'country' || field === 'state') && geo && geo[field as GeoField].length > 0) {
    return { kind: 'suggestions', options: geo[field as GeoField].map((suggestion) => ({ label: suggestion.label, values: suggestion.variants })) };
  }
  return FREE_TEXT;
}

const sameSet = (a: readonly string[], b: readonly string[]) => {
  const left = [...new Set(a)].sort();
  const right = [...new Set(b)].sort();
  return left.length === right.length && left.every((value, index) => value === right[index]);
};

/** The option whose values are exactly these stored values, if any. */
export function optionForValues(options: readonly ValueOption[], values: readonly string[]): ValueOption | null {
  const stored = values.filter(Boolean);
  if (!stored.length) return null;
  return options.find((option) => sameSet(option.values, stored)) ?? null;
}

/** True when every value of the option is stored, so its row reads as picked. */
export function isOptionPicked(option: ValueOption, values: readonly string[]): boolean {
  return option.values.every((value) => values.includes(value));
}

/**
 * The stored values as chips: each option whose values are all stored becomes
 * one chip with its label; every other stored value is a raw chip.
 */
export function chipsFor(options: readonly ValueOption[], values: readonly string[]): ValueChip[] {
  const stored = [...new Set(values.filter(Boolean))];
  const consumed = new Set<string>();
  const chips: ValueChip[] = [];
  for (const option of options) {
    if (option.values.length && isOptionPicked(option, stored) && option.values.some((value) => !consumed.has(value))) {
      option.values.forEach((value) => consumed.add(value));
      chips.push({ key: `option:${option.label}`, label: option.label, values: option.values, known: true });
    }
  }
  for (const value of stored) {
    if (!consumed.has(value)) chips.push({ key: `raw:${value}`, label: value, values: [value], known: false });
  }
  return chips;
}

/** Picking an option adds all of its values; picking it again removes them. */
export function toggleOption(values: readonly string[], option: ValueOption): string[] {
  const stored = values.filter(Boolean);
  if (isOptionPicked(option, stored)) return stored.filter((value) => !option.values.includes(value));
  return [...new Set([...stored, ...option.values])].slice(0, 100);
}

/** Removing a chip removes every value it stands for. */
export function removeChip(values: readonly string[], chip: ValueChip): string[] {
  return values.filter((value) => value && !chip.values.includes(value));
}

/**
 * Choosing an option for "is" writes `eq` when it stands for one stored value and
 * `one-of` over all its values otherwise. "Is not" takes one value, so it offers
 * only single-value options (see `singleValueOptions`).
 */
export function conditionForOption(field: TextField, operator: 'eq' | 'neq', option: ValueOption): TextCondition {
  if (operator === 'neq' || option.values.length === 1) return { type: 'text', field, operator, values: [option.values[0]!] };
  return { type: 'text', field, operator: 'one-of', values: [...option.values] };
}

export function singleValueOptions(options: readonly ValueOption[]): ValueOption[] {
  return options.filter((option) => option.values.length === 1);
}

/** Options whose label contains the typed text, ignoring case. */
export function filterOptions(options: readonly ValueOption[], query: string): ValueOption[] {
  const q = query.trim().toLowerCase();
  return q ? options.filter((option) => option.label.toLowerCase().includes(q)) : [...options];
}

/** Stored values in the dashboard's words for a sentence: option labels, and raw values no option covers. */
export function valueWords(options: readonly ValueOption[] | undefined, values: readonly string[]): string[] {
  return options?.length ? chipsFor(options, values).map((chip) => chip.label) : values.filter(Boolean);
}
